"use client";
import { motion } from "framer-motion";

// Clean color palette - Only Deep Teal Green and White
const colors = {
  primary: "#395750",      // Deep Teal Green - Company color
  primaryDark: "#2A423C",  // Darker teal
  primaryLight: "#4B6B63", // Lighter teal
  white: "#FFFFFF",        // White
  whiteOff: "#F8F9FA",     // Off-white for subtle contrast
  textDark: "#1A2E29",     // Dark text for contrast
};

const WhatWeDo = () => {
  return (
    <section className="relative overflow-hidden rounded-lg shadow-2xl overflow-x-hidden w-full" style={{ backgroundColor: colors.white }}>
      <div className="absolute inset-0 opacity-40" style={{ background: `linear-gradient(to bottom right, ${colors.primary}10, ${colors.white})` }} />

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full"
        >
          <div className="mt-6 sm:mt-9" />
          
          <div className="grid items-center gap-8 gap-y-12 lg:grid-cols-[1fr_auto_1fr] lg:gap-16 w-full">
            {/* Image */}
            <motion.div
              className="relative w-full h-64 sm:h-80 md:h-full overflow-hidden rounded-lg shadow-lg"
              whileHover={{ scale: 1.05 }}
            >
              <img
                src="/images/about/who1.jpg"
                alt="Yegna Microfinance Team"
                className="w-full h-full rounded-lg object-cover shadow-lg"
              />
            </motion.div>

            {/* Separator */}
            <div className="relative flex justify-center">
              <div className="absolute left-1/2 top-1/2 hidden h-36 w-[3px] -translate-y-1/2 bg-gradient-to-b from-transparent via-primary to-transparent md:block" style={{ background: `linear-gradient(to bottom, transparent, ${colors.primary}, transparent)` }} />
            </div>

            {/* Description */}
            <motion.div
              className="space-y-4 sm:space-y-6 w-full"
              whileInView={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 50 }}
              transition={{ duration: 0.6 }}
            >
              <p className="rounded-md p-3 sm:p-4 text-base sm:text-lg shadow-md text-justify hyphens-auto w-full" style={{ backgroundColor: colors.white, color: colors.textDark }}>
                <span className="font-semibold" style={{ color: colors.primary }}>
                  Yegna Microfinance Institution S.C.
                </span>{" "}
                was established to support national economic growth by providing
                customized financial products. It primarily serves micro, small,
                and medium businesses, addressing financial gaps for low-income
                entrepreneurs while ensuring profitability and sustainability.
              </p>
              <p className="rounded-md p-3 sm:p-4 text-base sm:text-lg shadow-md text-justify hyphens-auto w-full" style={{ backgroundColor: colors.white, color: colors.textDark }}>
                Licensed by the <strong>National Bank of Ethiopia</strong>,
                Yegna Microfinance started operations in 2022 with an initial
                capital of <strong>Birr 40 Million</strong>. The institution is
                backed by committed and experienced shareholders, working
                closely with governmental and financial organizations to expand
                financial inclusivity.
              </p>
              <div className="rounded-md p-3 sm:p-4 text-base sm:text-lg shadow-md w-full" style={{ backgroundColor: colors.white, color: colors.textDark }}>
                <p>
                  The <strong>logo</strong> represents sustainability, support,
                  and financial inclusiveness:
                </p>
                <ul className="mt-2 space-y-1 sm:space-y-0 list-disc pl-6">
                  <li>
                    <span className="font-semibold" style={{ color: colors.primary }}>
                      Green color
                    </span>{" "}
                    symbolizes sustainable growth.
                  </li>
                  <li>
                    <span className="font-semibold" style={{ color: colors.primary }}>
                      Hand symbols
                    </span>{" "}
                    represent financial support for low-income communities.
                  </li>
                  <li>
                    <span className="font-semibold" style={{ color: colors.primary }}>
                      Round shape
                    </span>{" "}
                    signifies inclusivity in the financial system.
                  </li>
                  <li>
                    <span className="font-semibold" style={{ color: colors.primary }}>
                      'Y' symbol
                    </span>{" "}
                    reflects belonging and empowerment.
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>

          <motion.h2
            initial={{ opacity: 1, y: 0 }}
            className="mb-6 sm:mb-8 mt-12 sm:mt-16 text-center text-3xl sm:text-4xl lg:text-5xl font-bold"
            style={{ color: colors.primary }}
          >
            <span className="relative inline-block">
              <span className="bg-gradient-to-r bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}>
                What We Do
              </span>
              <span className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r" style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}></span>
            </span>
          </motion.h2>

          <div className="grid items-center gap-8 gap-y-12 lg:grid-cols-[1fr_auto_1fr] lg:gap-16 w-full">
            <motion.div
              className="space-y-4 sm:space-y-8 text-center sm:text-left w-full"
              whileInView={{ opacity: 1, x: 0 }}
              initial={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-xl sm:text-2xl font-semibold drop-shadow-md" style={{ color: colors.primary }}>
                Financial Empowerment
              </h3>
              <p className="rounded-md p-3 sm:p-4 text-base sm:text-lg shadow-md w-full" style={{ backgroundColor: colors.white, color: colors.textDark }}>
                We provide <strong>innovative financial services</strong> and{" "}
                empower the community.
              </p>
            </motion.div>

            <div className="relative flex justify-center">
              <div className="top-1/10 absolute left-1/2 hidden h-36 w-[3px] -translate-y-1/2 bg-gradient-to-b from-transparent to-transparent md:block" style={{ background: `linear-gradient(to bottom, transparent, ${colors.primary}, transparent)` }} />
            </div>

            <motion.div
              className="flex h-full w-full"
              whileHover={{ scale: 1.05 }}
            >
              <img
                src="/images/about/whatwedo.jpg"
                alt="Our Name Image"
                className="w-full h-auto max-h-[250px] sm:max-h-[350px] lg:max-h-[400px] rounded-lg object-cover shadow-xl grayscale transition-all duration-300 hover:grayscale-0"
              />
            </motion.div>
          </div>

          <div className="mt-12 sm:mt-16" />
          
          <motion.h2
            initial={{ opacity: 1, y: 0 }}
            className="mb-6 sm:mb-8 mt-12 sm:mt-16 text-center text-3xl sm:text-4xl lg:text-5xl font-bold"
            style={{ color: colors.primary }}
          >
            <span className="relative inline-block">
              <span className="bg-gradient-to-r bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}>
                OUR NAME
              </span>
              <span className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r" style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}></span>
            </span>
          </motion.h2>

          <div className="mt-6 sm:mt-9" />
          
          <div className="grid items-center gap-8 gap-y-12 lg:grid-cols-[1fr_auto_1fr] lg:gap-16 w-full">
            <div className="relative flex h-64 w-64 sm:h-80 sm:w-80 md:h-96 md:w-96 mx-auto lg:mx-0 items-center justify-center rounded-full shadow-xl" style={{ border: `4px solid ${colors.primary}20` }}>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "linear",
                }}
                className="absolute inset-0 flex items-center justify-center"
              >
                <div className="absolute h-full w-full rounded-full border-2 border-dashed" style={{ borderColor: `${colors.primary}40` }} />
              </motion.div>

              <div className="relative z-10 text-center">
                <span className="text-2xl sm:text-3xl md:text-4xl font-semibold" style={{ color: colors.primary }}>
                  Yegna
                </span>
                <p className="mt-2 text-sm sm:text-base md:text-lg" style={{ color: colors.textDark }}>Microfinance S.C</p>
              </div>

              <div className="absolute inset-0 rounded-full blur-lg" style={{ backgroundColor: `${colors.primary}10` }} />
            </div>
            
            <div className="relative flex justify-center">
              <div className="absolute left-1/2 top-1/2 hidden h-36 w-[3px] -translate-y-1/2 bg-gradient-to-b from-transparent to-transparent md:block" style={{ background: `linear-gradient(to bottom, transparent, ${colors.primary}, transparent)` }} />
            </div>

            <motion.div
              className="space-y-4 sm:space-y-6 px-4 md:px-8 text-center lg:text-left w-full"
              whileInView={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 50 }}
              transition={{ duration: 0.6 }}
            >
              <p className="rounded-md p-3 sm:p-4 text-base sm:text-lg shadow-md w-full" style={{ backgroundColor: colors.white, color: colors.textDark }}>
                The Name{" "}
                <span className="font-semibold" style={{ color: colors.primary }}>
                  Yegna Microfinance S.C
                </span>{" "}
                symbolizes our commitment to{" "}
                <strong>financial empowerment</strong>,{" "}
                <strong>community growth</strong>, and{" "}
                <strong>sustainable development</strong>.
              </p>
            </motion.div>
          </div>

          {/* Our Impact */}
          <motion.h3
            initial={{ opacity: 1, y: 0 }}
            className="mb-6 sm:mb-8 mt-12 sm:mt-16 text-center text-3xl sm:text-4xl lg:text-5xl font-bold"
            style={{ color: colors.primary }}
          >
            <span className="relative inline-block">
              <span className="bg-gradient-to-r bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}>
                Our Impact
              </span>
              <span className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r" style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}></span>
            </span>
          </motion.h3>

          <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 md:grid-cols-3 w-full" style={{ color: colors.textDark }}>
            {[
              "Financial Inclusion",
              "Loans Disbursed",
              "Customers Served",
              "Sustainability",
              "Community Development",
              "Job Creation",
            ].map((item, index) => (
              <motion.div
                key={index}
                className="flex items-start gap-2 rounded-lg border-l-4 p-3 sm:p-4 shadow-lg hover:shadow-2xl text-sm sm:text-base w-full"
                style={{ 
                  borderLeftColor: colors.primary,
                  backgroundColor: colors.white
                }}
                whileHover={{ scale: 1.05 }}
              >
                <svg
                  className="mt-1 flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5"
                  viewBox="0 0 24 24"
                  fill={colors.primary}
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M20.285 6.709L9 18l-5.285-5.291L5.707 11.293 9 14.586l10.578-10.577z" />
                </svg>
                <span>{item}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhatWeDo;